import { clearOpsSession, getOpsAccessToken, OpsOAuthError } from "./oauth.ts";

export interface ApiErrorPayload {
  code: string;
  message: string;
  retryable?: boolean;
  details?: Record<string, unknown>;
  requestId?: string;
  traceId?: string;
}

export class OpsApiError extends Error {
  constructor(
    readonly status: number,
    readonly details: ApiErrorPayload
  ) {
    super(details.message);
    this.name = "OpsApiError";
  }
}

export function retryOpsQuery(failureCount: number, error: unknown): boolean {
  return error instanceof OpsApiError && error.details.retryable === true && failureCount < 5;
}

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const accessToken = await readAccessToken();
  if (!accessToken) {
    throw new OpsApiError(401, { code: "SESSION_REQUIRED", message: "需要重新登录" });
  }
  const response = await fetch(path, {
    ...init,
    cache: "no-store",
    headers: {
      accept: "application/json",
      authorization: `Bearer ${accessToken}`,
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers
    }
  });
  if (response.status === 401) clearOpsSession();
  if (response.status === 204) return undefined as T;
  const body: unknown = await response.json().catch(() => {
    if (response.ok) {
      throw new OpsApiError(response.status, {
        code: "INVALID_API_RESPONSE",
        message: "接口未返回有效的 JSON 响应"
      });
    }
    return null;
  });
  if (!response.ok) {
    const details = readError(body) ?? {
      code: "HTTP_ERROR",
      message: `请求失败，HTTP ${response.status}`
    };
    throw new OpsApiError(response.status, details);
  }
  return body as T;
}

export async function authenticatedFetch(
  input: string | URL | Request,
  init?: RequestInit
): Promise<Response> {
  const accessToken = await readAccessToken();
  if (!accessToken) {
    throw new OpsApiError(401, { code: "SESSION_REQUIRED", message: "需要重新登录" });
  }
  const headers = new Headers(init?.headers);
  headers.set("authorization", `Bearer ${accessToken}`);
  const response = await fetch(input, { ...init, headers });
  if (response.status === 401) clearOpsSession();
  return response;
}

export function queryString(
  values: Record<string, string | number | boolean | null | undefined>
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

async function readAccessToken(): Promise<string | null> {
  try {
    return await getOpsAccessToken();
  } catch (error) {
    if (error instanceof OpsOAuthError && error.retryable) {
      throw new OpsApiError(503, {
        code: "SESSION_REFRESH_UNAVAILABLE",
        message: "登录状态暂时无法刷新",
        retryable: true
      });
    }
    throw error;
  }
}

function readError(value: unknown): ApiErrorPayload | null {
  if (!value || typeof value !== "object") return null;
  const error = (value as { error?: unknown }).error;
  if (!error || typeof error !== "object") return null;
  const fields = error as Record<string, unknown>;
  const diagnostics = isRecord(fields.details)
    ? fields.details
    : Object.fromEntries(
        Object.entries(fields).filter(
          ([key]) => !["code", "message", "retryable", "requestId", "traceId"].includes(key)
        )
      );
  return typeof fields.code === "string" && typeof fields.message === "string"
    ? {
        code: fields.code,
        message: fields.message,
        ...(typeof fields.retryable === "boolean" ? { retryable: fields.retryable } : {}),
        ...(Object.keys(diagnostics).length > 0 ? { details: diagnostics } : {}),
        ...(typeof fields.requestId === "string" ? { requestId: fields.requestId } : {}),
        ...(typeof fields.traceId === "string" ? { traceId: fields.traceId } : {})
      }
    : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}
